import { parseISO } from "date-fns";
import { useEffect, useState } from "react";

const TIMER_RADIUS = 30;

const getValue = (duration, target) => {
  if (target) {
    const targetDate = parseISO(target);
    const timeLeft = targetDate - Date.now();
    const init = Math.round(timeLeft / 1000);
    return Math.min(init, duration);
  } else {
    return duration;
  }
};

/**
 * Shows a countdown timer progress bar.
 *
 * @param {Number} duration duration in seconds.
 * @param {Date} target date and time for countdown.
 * @param {Boolean} urgent turn the timer red and large in the last 5 seconds.
 * @param {Boolean} reversed Use a recharging timer effect (smaller and monochrome).
 * @param {Boolean} textOnly Only render the countdown number.
 */
const Timer = ({
  duration,
  target,
  urgent = false,
  reversed = false,
  textOnly = false,
  className = "",
}) => {
  const [lastTarget, setLastTarget] = useState(target);
  const [value, setValue] = useState(() => getValue(duration, target));
  const [clearCircle, setClearCircle] = useState(false);

  useEffect(() => {
    if (target !== lastTarget) {
      // restart the timer
      setValue(getValue(duration, target));
      setLastTarget(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration, target]);

  useEffect(() => {
    // Instantly clear the progress circle when reversed is true
    if (reversed) {
      setClearCircle(true);
      setTimeout(() => {
        setClearCircle(false);
      });
    }
  }, [reversed]);

  const percent =
    100 - (Math.max(0, reversed ? duration - value : value) / duration) * 100;
  const circumference = TIMER_RADIUS * 2 * Math.PI;

  useEffect(() => {
    if (value === duration) {
      // Immediately start animating
      setValue((v) => v - 1);
    }
    if (value >= 0) {
      const interval = setTimeout(() => setValue((v) => v - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [value, duration]);

  if (textOnly) {
    return value + 1;
  }

  return (
    <div
      className={`relative inline-flex items-center justify-center overflow-hidden rounded-full ${className}`}
    >
      {/* Building a Progress Ring: https://css-tricks.com/building-progress-ring-quickly/ */}
      <svg
        className={`transition-all ${
          reversed ? "w-10 h-10" : "w-20 h-20"
        } -rotate-90`}
        viewBox="0 0 80 80"
      >
        <circle
          className="text-gray-300"
          strokeWidth="5"
          stroke="currentColor"
          fill="transparent"
          r={TIMER_RADIUS}
          cx="40"
          cy="40"
        />
        <circle
          className={`${
            reversed ? "text-gray-400" : "text-primary-600"
          } transition-all ${
            clearCircle ? "duration-[0ms]" : "duration-1000"
          } ease-linear`}
          strokeWidth="5"
          strokeDasharray={circumference}
          strokeDashoffset={(percent / -100) * circumference}
          strokeLinecap="round"
          stroke="currentColor"
          fill="transparent"
          r={TIMER_RADIUS}
          cx="40"
          cy="40"
        />
      </svg>
      <span
        className={`absolute tabular-nums transition origin-center ${
          urgent && value < 5
            ? "text-2xl text-red-700 animate-heartbeat"
            : reversed
            ? "text-sm text-gray-600"
            : "text-xl text-primary-700"
        }`}
      >
        {value + 1}
      </span>
      {urgent && value < 5 ? (
        <span
          className={`absolute tabular-nums transition origin-center text-2xl text-red-700 animate-ping`}
        >
          {value + 1}
        </span>
      ) : null}
    </div>
  );
};
export default Timer;
